import { makeStyles } from '@material-ui/styles'
import { AppPlugins, imageUrlRoot } from 'app/constants'
import clsx from 'clsx'
import Text from 'core/elements/Text'
import { PluginSection } from 'core/plugins/model'
import Theme from 'core/themes/model'
import React, { useEffect, useRef } from 'react'
import PluginCard from './PluginCard'
import SpinLogo from './SpinLogo'
// usePluginRouter removed - currentPluginId already passed as prop
import { getProductName, isOpenCloud } from 'core/utils/helpers'

const PLUGIN_COLOR_MAP = {
  [AppPlugins.OpenStack]: '#3ACCC533',
  [AppPlugins.OpenCloud]: '#3ACCC533',
  [AppPlugins.Kubernetes]: '#326DE633',
}

interface PluginMenuProps {
  open: boolean
  toggleMenu: () => void
  currentPluginId: string
  options: PluginSection[]
  onPluginChange?: (pluginId: string) => void
  collapse?: boolean
}

export default function PluginMenu({
  open,
  toggleMenu,
  onPluginChange,
  collapse = false,
  options,
  currentPluginId,
}: PluginMenuProps) {
  // currentPluginId now used from props
  const isAccountPlugin = currentPluginId === AppPlugins.MyAccount
  const classes = useStyles()

  // When users click away from the plugins drop down it should close
  const divRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(event.target as Node) && open) {
        toggleMenu()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  return (
    <div
      className={clsx(classes.pluginMenuContainer, {
        [classes.pluginMenuContainerOpen]: open,
        [classes.pluginMenuContainerAccount]: isAccountPlugin,
      })}
      ref={divRef}
    >
      <div
        className={clsx(classes.anchor, {
          [classes.anchorCollapsed]: collapse,
        })}
      >
        {isOpenCloud() ? (
          <img
            src={`${imageUrlRoot}/opencloud/r-Icon-White-Square.png`}
            alt="rackspace logo"
            className={classes.rackspaceLogo}
          />
        ) : (
          <SpinLogo active={false} />
        )}
        <Text
          variant="subtitle1"
          className={clsx(classes.productTitle, {
            [classes.productTitleCollapsed]: collapse,
          })}
        >
          {getProductName()}
        </Text>
      </div>
      {open && (
        <div className={classes.menu}>
          {options?.map((option) => {
            if (option.id === AppPlugins.MyAccount) return null
            return (
              <PluginCard
                key={option.id}
                title={option.name}
                description={option.description}
                onClick={() => onPluginChange(option.id)}
                icon={option.icon}
                active={false}
                color={PLUGIN_COLOR_MAP[option.id]}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  pluginMenuContainer: {
    gridArea: 'nav-header',
    '& .menu-popover': {
      padding: 8,
      backgroundColor: theme.components.sidebar.background,
      borderColor: theme.components.sidebar.border,
    },
    backgroundColor: theme.components.sidebar.background,
    borderBottom: `1px solid ${theme.components.sidebar.border}`,
    marginLeft: 0,
  },
  pluginMenuContainerOpen: {
    borderBottom: 'none',
  },
  pluginMenuContainerAccount: {
    marginLeft: '-72px',
  },
  anchor: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr max-content',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    justifyItems: 'stretch',
    paddingLeft: theme.spacing(2),
    height: 64,
    '&.active': {
      background: theme.components.card.background,
    },
    '& #logoDefault': {
      justifySelf: 'center',
    },
  },
  anchorCollapsed: {
    gridTemplateColumns: 'auto',
  },
  productTitle: {
    color: `${theme.components.sidebar.activeText} !important`,
    textTransform: 'capitalize',
    display: 'unset',
    whiteSpace: 'nowrap',
    fontSize: `18px !important`,
  },
  productTitleCollapsed: {
    display: 'none',
  },
  menuContainer: {
    '& .menu-popover': {
      padding: '8px',
      top: 'unset',
      left: 'unset',
    },
  },
  menu: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
    zIndex: 100,
    position: 'absolute',
    padding: theme.spacing(3),
    backgroundColor: theme.components.card.background,
  },
  rackspaceLogo: {
    width: '25px',
    height: '25px',
  },
}))
